import { getMostRecentSubmissionStatus, getVehicles } from 'actions/vehicles'
import { InsuranceFailureModal } from 'components/pages/account/DrivingInfo/VehicleInfo/InsuranceSubmission'
import { initialModalOptions } from 'types/global'
import { useContext, createContext, useState, ReactNode, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { DrivingProfile } from 'types/profile'

type PollingStatus = 'idle' | 'polling' | 'accepted' | 'rejected'

export type SubmissionResponse = {
  status: string
  ocr_failure?: boolean
  invalid_vin_found?: string | null
  no_vin_found?: boolean
  name_failure?: boolean
  insurance_expired?: boolean
  unsupported_category?: boolean
  duplicate_vin?: boolean
  plate_vin_mismatch?: boolean
}

type InsuranceContextType = {
  status: PollingStatus
  vehicleId: number
  attemptCount: number
  response: SubmissionResponse | undefined
  startPolling: (vehicleId: number) => void
  stopPolling: (status: PollingStatus) => void
}

const InsurancePollingContext = createContext<InsuranceContextType | undefined>(undefined)

export const useInsurancePollingContext = () => {
  const context = useContext(InsurancePollingContext)
  if (!context) {
    throw new Error('useInsurancePollingContext must be used within a InsurancePollingProvider')
  }
  return context
}

const InsurancePollingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const dispatch = useDispatch()
  const profile = useSelector<any, DrivingProfile>((state) => state.profile)
  const [modalOptions, setModalOptions] = useState(initialModalOptions)

  const [status, setStatus] = useState<PollingStatus>('idle')
  const [vehicleId, setVehicleId] = useState<number>(0)
  const [response, setResponse] = useState()
  const [attemptCount, setAttemptCount] = useState<number>(1)

  const intervalIdRef = useRef<NodeJS.Timeout | null>(null)
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null)

  const startPolling = (vehicle_id: number) => {
    setStatus('polling')
    if (vehicleId === vehicle_id) {
      setAttemptCount(attemptCount + 1)
    } else {
      setAttemptCount(1)
      setVehicleId(vehicle_id)
    }
    pollStatus(vehicle_id)
  }

  const stopPolling = (status: PollingStatus = 'idle') => {
    if (intervalIdRef.current) clearInterval(intervalIdRef.current)
    if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current)
    setStatus(status)
  }

  const pollStatus = (vehicle_id: number) => {
    intervalIdRef.current = setInterval(async () => {
      try {
        const res = await getMostRecentSubmissionStatus(vehicle_id)
        const data = await res?.data
        if (data) {
          if (data['status'] == 'accepted') {
            setModalOptions({ modalOpen: false, modalType: null })
            dispatch({
              type: 'PUSH_FLASH',
              flash: { messageKey: 'dweb_flash_proof_of_insurance_added', type: 'success' },
            })
            dispatch(getVehicles(profile.id))
            stopPolling('accepted')
          } else if (data['status'] == 'rejected') {
            setModalOptions({ modalOpen: false, modalType: null })
            setResponse(data)
            stopPolling('rejected')
            // If there's a plate_vin_mismatch, refresh vehicles to get latest info on conflict so driver can resolve it
            if (data['plate_vin_mismatch'] == true) {
              dispatch(getVehicles(profile.id))
            }
          }
        }
      } catch (err) {
        dispatch({
          type: 'PUSH_FLASH',
          flash: {
            messageKey: 'dweb_flash_insurance_upload_issue_try_again_later',
            type: 'alert',
          },
        })
      }
    }, 5000)

    timeoutIdRef.current = setTimeout(
      () => {
        stopPolling()
      },
      20 * 60 * 1000
    ) // 20 minute timeout
  }

  useEffect(() => {
    // Cleanup function
    return () => {
      stopPolling()
    }
  }, [])

  return (
    <InsurancePollingContext.Provider value={{ status, vehicleId, attemptCount, response, startPolling, stopPolling }}>
      {children}
      <InsuranceFailureModal modalOptions={modalOptions} setModalOptions={setModalOptions} />
    </InsurancePollingContext.Provider>
  )
}

export default InsurancePollingProvider
